import * as React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { Tooltip } from "src/library/components/Tooltip";
import { Icons } from "src/library/components/Icon/types";
import useCurrentUser from "src/core/users/hooks/useCurrentUser";
import { IOrderLine } from "src/core/orders/api/orders.api";

import {
  useCreditRequestContext,
  CreditRequestRowType
} from "../context/CreditRequestContext";
import { useCreditReasonCodes } from "../hooks/useCreditReasonCodes";
import { CreditQuantityField } from "./CreditQuantityField";

interface CreditRequestItemReasonProps {
  credit: CreditRequestRowType;
  orderLine: IOrderLine;
  removeReason: () => void;
  showApprovalColumn: boolean;
}

/**
 * Component to display and handle amends to one credit reason row
 */
export const CreditRequestItemReason: React.ReactFCWithChildren<CreditRequestItemReasonProps> = ({
  credit, removeReason, orderLine, showApprovalColumn
}) => {
  const creditReasons = useCreditReasonCodes();

  const [
    {
      mode, supportTicket, approveAll
    },
    { setCreditRequestRow }
  ] = useCreditRequestContext();

  const [ { currentRole } ] = useCurrentUser();

  // Credit reason code
  const reasonCode = React.useMemo(() => {
    return _.find(creditReasons, cr => cr.id === credit.creditRequestReasonCodeId);
  }, [ credit.creditRequestReasonCodeId, creditReasons ]);

  // check if edit is available to a user
  const canChangeQuantity = React.useMemo((): boolean => {
    if (mode !== "edit") {
      return false;
    } else if (!credit.localId && supportTicket?.id !== credit.supportTicketId) {
      return false;
    }

    return true;
  }, [
    credit.supportTicketId,
    credit.localId,
    mode,
    supportTicket
  ]);

  // check if edit is available to a user
  const canApproveQuantity = React.useMemo((): boolean => {
    if (mode !== "review") {
      return false;
    } else if (!credit.localId && supportTicket?.id !== credit.supportTicketId) {
      return false;
    } else if (approveAll) {
      return false;
    }

    return true;
  }, [
    mode,
    credit.localId,
    credit.supportTicketId,
    supportTicket?.id,
    approveAll
  ]);

  const externalTicketId = React.useMemo(() => {
    if (supportTicket && credit.supportTicketId && supportTicket.id !== credit.supportTicketId) {
      return credit.supportTicketId;
    }

    return null;
  }, [ credit, supportTicket ]);

  return (
    <>
      <CreditRequestItemReasonWrapper
        direction="horizontal"
        className="row-right"
      >
        <div className="requested-quantity-col credit-col">
          {canChangeQuantity ? (
            <CreditQuantityField
              value={credit.quantityRequested.toString()}
              onChange={e => setCreditRequestRow({
                ...credit,
                quantityRequested: parseInt(e.currentTarget.value, 10)
              }, orderLine)}
            />
          ) : (
            <BorderedBox className="quantity">
              <Text>
                {credit.quantityRequested}
              </Text>
            </BorderedBox>
          )}
        </div>

        <div className="reason-col credit-col">
          <BorderedBox className="reason-name">
            <Text>
              {reasonCode?.name}
            </Text>
          </BorderedBox>

          {canChangeQuantity && (
            <div
              className="reason-icon remove-icon"
              onClick={() => removeReason()}
            >
              <Icon
                icon={Icons.CLOSE}
                size={20}
                $hoverColor="midGrey"
                $fillColor="lightGrey"
              />
            </div>
          )}

          {externalTicketId && (
            <>
              <div
                data-tip
                data-for={`${externalTicketId}-new-tab-tip`}
                className="reason-icon other-ticket-icon"
                onClick={() => window.open(`/support/tickets/${externalTicketId}`, "_blank")}
              >
                <Icon
                  icon={Icons.OPENNEW}
                  size={20}
                  $hoverColor="midGrey"
                  $fillColor="lightGrey"
                />
              </div>

              <Tooltip
                id={`${externalTicketId}-new-tab-tip`}
                type="dark"
                place="top"
                getContent={() => {
                  return (
                    <Text color="white">
                      This product issue was already reported in a different support ticket. To change the quantity please amend this ticket.
                    </Text>
                  );
                }}
              />
            </>
          )}
        </div>

        {canApproveQuantity && currentRole?.type === "admin" ? (
          <div className="approved-quantity-col credit-col">
            <CreditQuantityField
              value={credit.quantityApproved?.toString() || ""}
              onChange={e => setCreditRequestRow({
                ...credit,
                quantityApproved: parseInt(e.currentTarget.value, 10)
              }, orderLine)}
            />
          </div>
        // if quantity approved is set then display it
        ) : showApprovalColumn && (
          <div className="approved-quantity-col credit-col">
            <BorderedBox className="quantity">
              <Text>
                {typeof credit.quantityApproved === "number" ? credit.quantityApproved : "-"}
              </Text>
            </BorderedBox>
          </div>
        )}

      </CreditRequestItemReasonWrapper>

      {!!credit.error && (
        <ReasonError>
          <Text color="error">
            {credit.error}
          </Text>
        </ReasonError>
      )}

    </>
  );
};

const ReasonError = styled.div`
  ${({ theme }) => css`
  border-top: 1px solid ${theme.colors.error};
  padding: ${theme.spacing.padding.sm};
  margin-left: ${theme.spacing.padding.sm};
`}`;

const CreditRequestItemReasonWrapper = styled(Grid.Bounds)`  
  width: 100%!important;

  .reason-col {
    display: flex;

    .reason-icon {
      display: flex;
      align-items: center;
      width: 25px;
      margin-left: 5px;

      &:hover {
        cursor: pointer;
      }
    }

    .reason-name {
      width: 170px;
    }
  }
`;

const BorderedBox = styled.div`
  ${({ theme }) => css`
    border: solid 1px ${theme.colors.lightGrey};
    border-radius: ${theme.borderRadius};
    padding: ${theme.spacing.padding.sm};


    &.quantity {
      text-align: center;
      width: 40px;
    }
  `}
`;
